/**
 * Frontend source of truth for Hong Kong university locations shown on the
 * landing-page Partners map, plus utilities that group societies by campus.
 *
 * All data is baked into the frontend (no API round-trip):
 * - `UNIVERSITY_LOCATIONS`: campus coordinates + display names for the map.
 * - `SOCIETY_UNIVERSITY_OVERRIDES`: corrections for societies whose stored
 *   `university` code is inaccurate. Example: the HKUST societies are stored
 *   under `hku` in the DB, and 崇基劇社 (Chung Chi College) is stored as null,
 *   even though it belongs to CUHK.
 * - `groupSocietiesByUniversity`: folds `STATIC_SOCIETIES` into ordered
 *   university groups (an "Others" bucket holds any unmapped society).
 */
import type { StaticSociety } from "./societies";

export type UniversityLocation = {
  code: string;
  name: string;
  nameZh: string;
  short: string;
  lat: number;
  lng: number;
};

/** The ten universities whose campus locations are pinned on the map. */
export const UNIVERSITY_LOCATIONS: UniversityLocation[] = [
  { code: "hku", name: "The University of Hong Kong", nameZh: "香港大學", short: "HKU", lat: 22.2824, lng: 114.1394 },
  { code: "cuhk", name: "The Chinese University of Hong Kong", nameZh: "香港中文大學", short: "CUHK", lat: 22.4189, lng: 114.2067 },
  { code: "hkust", name: "The Hong Kong University of Science and Technology", nameZh: "香港科技大學", short: "UST", lat: 22.3384, lng: 114.264 },
  { code: "cityu", name: "City University of Hong Kong", nameZh: "香港城市大學", short: "CityU", lat: 22.3369, lng: 114.1729 },
  { code: "polyu", name: "The Hong Kong Polytechnic University", nameZh: "香港理工大學", short: "PolyU", lat: 22.3041, lng: 114.1805 },
  { code: "hkbu", name: "Hong Kong Baptist University", nameZh: "香港浸會大學", short: "HKBU", lat: 22.3355, lng: 114.1813 },
  { code: "eduhk", name: "The Education University of Hong Kong", nameZh: "香港教育大學", short: "EdUHK", lat: 22.4229, lng: 114.2198 },
  { code: "lingu", name: "Lingnan University", nameZh: "嶺南大學", short: "LU", lat: 22.4072, lng: 114.0108 },
  { code: "hkmu", name: "Hong Kong Metropolitan University", nameZh: "香港都會大學", short: "HKMU", lat: 22.3099, lng: 114.1815 },
  { code: "hksyu", name: "Hong Kong Shue Yan University", nameZh: "香港樹仁大學", short: "HKSYU", lat: 22.2915, lng: 114.1933 },
];

export const UNI_BY_CODE: Record<string, UniversityLocation> = Object.fromEntries(
  UNIVERSITY_LOCATIONS.map((uni) => [uni.code, uni]),
) as Record<string, UniversityLocation>;

/** Name-based corrections applied on top of the stored `university` code. */
export const SOCIETY_UNIVERSITY_OVERRIDES: Record<string, string> = {
  "HKUST MBA": "hkust",
  "University YMCA 大學青年會（科大）": "hkust",
  "Aurelia, 34th UST Danso 🪼": "hkust",
  "崇基劇社": "cuhk",
};

/** Raw DB code aliases that are really the canonical UNIVERSITIES codes. */
const UNIVERSITY_CODE_ALIASES: Record<string, string> = {
  bu: "hkbu",
  ust: "hkust",
};

export type SocietyGroup = {
  code: string | null;
  location: UniversityLocation | null;
  items: StaticSociety[];
};

export function universityCodeFor(society: StaticSociety): string | null {
  const overridden = SOCIETY_UNIVERSITY_OVERRIDES[society.name];
  if (overridden) return overridden;
  if (!society.university) return null;
  return UNIVERSITY_CODE_ALIASES[society.university] ?? society.university;
}

/**
 * Group a society list by university, ordered by `UNIVERSITY_LOCATIONS`,
 * with a trailing bucket for societies that map to no campus.
 */
export function groupSocietiesByUniversity(societies: StaticSociety[]): SocietyGroup[] {
  const buckets = new Map<string | null, StaticSociety[]>();
  for (const society of societies) {
    const code = universityCodeFor(society);
    if (!buckets.has(code)) buckets.set(code, []);
    buckets.get(code)!.push(society);
  }

  const groups: SocietyGroup[] = UNIVERSITY_LOCATIONS.filter((uni) => buckets.has(uni.code)).map(
    (uni) => ({
      code: uni.code,
      location: uni,
      items: buckets.get(uni.code)!,
    }),
  );

  const others = buckets.get(null);
  if (others) {
    groups.push({ code: null, location: null, items: others });
  }
  return groups;
}